import { useMemo, useCallback } from 'react';

import { useMetadataQuery } from '@/generated/graphql';
import { useAccountContext } from '@/lib/accounts/account-context';
import { FullPageLoader } from '@/components/loader';
import { buildExplorations } from '@/core/exploration/builder/list';
import { convertExploration, convertMetricTypes, convertModelTypes } from '@/explore/input';

import { getModelOrThrow } from '../explore/model/utils';
import { MetadataContext } from './metadata-context';

interface MetadataContextProviderProps {
  children: React.ReactNode;
}

// The purpose of this context is to provide customer configuration to downstream components. For example,
// explorations and models.
export const MetadataContextProvider = (props: MetadataContextProviderProps) => {
  const { account } = useAccountContext();

  const { data, loading } = useMetadataQuery({
    variables: { accountId: account.accountId },
  });

  const { models, metrics, explorations } = useMemo(() => {
    const models = convertModelTypes(data?.account?.models) ?? [];
    const metrics = convertMetricTypes(data?.account?.metrics) ?? [];

    const explorationMetadata = (data?.account?.explorationMetadata ?? []).map(convertExploration);

    const explorations = buildExplorations({
      models,
      savedExplorations: explorationMetadata.filter(({ parameters }) => parameters.length === 0),
      savedDetailExplorations: explorationMetadata.filter(
        ({ parameters }) => parameters.length > 0,
      ),
    });

    return { models, metrics, explorations };
  }, [data?.account]);

  const getModel = useCallback((modelId: string) => getModelOrThrow(models, modelId), [models]);

  const contextValue = useMemo(
    () => ({ models, explorations, metrics, getModel }),
    [models, explorations, metrics, getModel],
  );

  if (data === undefined && loading && location.pathname !== '/signup') {
    return <FullPageLoader />;
  }

  return <MetadataContext.Provider value={contextValue}>{props.children}</MetadataContext.Provider>;
};
